import { useCallback, useMemo } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { SelectedMultiOption } from '@weareopenr/component-library/build/components/MultiSelect/utilities';
import { usePriceBands } from 'src/hooks/usePriceBands';
import { PRICE_BAND_FILTER_KEY } from 'src/constants';
import { IPriceBandWithVenues } from 'src/types/priceBand.types';

export const usePriceBandFilter = (priceSchemeId: string) => {
  const { priceBands, isLoading, isFetching } = usePriceBands(priceSchemeId);
  const [deselectedPriceBands, setDeselectedPriceBands] = useSessionStorage<string[]>(PRICE_BAND_FILTER_KEY, []);

  const priceBandOptions: SelectedMultiOption[] = useMemo(
    () =>
      priceBands
        .filter((band): band is IPriceBandWithVenues & { priceBandId: string } => band.priceBandId !== null)
        .map(({ priceBandId, name }, index) => ({ id: priceBandId, label: name, display_order: index })),
    [priceBands],
  );

  const selectedPriceBands = useMemo(
    () => priceBandOptions.map(({ id }) => id).filter((id) => !deselectedPriceBands.includes(id)),
    [priceBandOptions, deselectedPriceBands],
  );

  const setSelectedPriceBands = useCallback(
    (selected: string[]) => {
      const optionIds = priceBandOptions.map(({ id }) => id);
      setDeselectedPriceBands((stored) => [
        ...stored.filter((id) => !optionIds.includes(id)),
        ...optionIds.filter((id) => !selected.includes(id)),
      ]);
    },
    [priceBandOptions, setDeselectedPriceBands],
  );

  const visiblePriceBands = useMemo(
    () => priceBands.filter(({ priceBandId }) => priceBandId === null || selectedPriceBands.includes(priceBandId)),
    [priceBands, selectedPriceBands],
  );

  const searchPriceBands = useMemo(
    () => (selectedPriceBands.length < priceBandOptions.length ? selectedPriceBands : []),
    [selectedPriceBands, priceBandOptions],
  );

  return {
    priceBands,
    visiblePriceBands,
    priceBandOptions,
    selectedPriceBands,
    setSelectedPriceBands,
    searchPriceBands,
    hasNoPriceBandsSelected: priceBandOptions.length > 0 && selectedPriceBands.length === 0,
    isLoading,
    isFetching,
  };
};
